import React, { useState, useEffect } from 'react';
import api from '../services/api';

const ConnectionTest = () => {
  const [results, setResults] = useState({
    loading: true,
    healthCheck: null,
    apiUrl: process.env.REACT_APP_API_URL || 'http://localhost:5000/api',
    environment: process.env.NODE_ENV,
    error: null
  });

  useEffect(() => {
    testConnection();
  }, []);

  const testConnection = async () => {
    console.log('🧪 Starting connection test...');
    setResults(prev => ({ ...prev, loading: true, error: null }));

    try {
      // Test health endpoint
      console.log('📡 Testing /api/health endpoint...');
      const response = await api.get('/health');
      
      console.log('✅ Health check successful:', response.data);
      setResults(prev => ({
        ...prev,
        loading: false,
        healthCheck: response.data,
        error: null
      }));
    } catch (error) {
      console.error('❌ Connection test failed:', error);
      setResults(prev => ({
        ...prev,
        loading: false,
        error: {
          message: error.message,
          code: error.code,
          response: error.response?.data,
          status: error.response?.status
        }
      }));
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 p-8">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h1 className="text-3xl font-bold mb-6 text-gray-800">
            🔌 Backend Connection Test
          </h1>

          {/* Configuration Info */}
          <div className="mb-6 p-4 bg-blue-50 rounded-lg">
            <h2 className="font-semibold text-lg mb-2">Configuration</h2>
            <div className="space-y-2 text-sm">
              <p><strong>API URL:</strong> {results.apiUrl}</p>
              <p><strong>Environment:</strong> {results.environment}</p>
              <p><strong>Timestamp:</strong> {new Date().toLocaleString()}</p>
            </div>
          </div>

          {/* Test Results */}
          {results.loading && (
            <div className="p-4 bg-yellow-50 rounded-lg">
              <p className="text-yellow-800">🔄 Testing connection...</p>
            </div>
          )}

          {!results.loading && results.healthCheck && (
            <div className="p-4 bg-green-50 rounded-lg mb-4">
              <h2 className="font-semibold text-lg mb-2 text-green-800">
                ✅ Connection Successful!
              </h2>
              <pre className="bg-white p-3 rounded text-xs overflow-auto">
                {JSON.stringify(results.healthCheck, null, 2)}
              </pre>
            </div>
          )}

          {!results.loading && results.error && (
            <div className="p-4 bg-red-50 rounded-lg mb-4">
              <h2 className="font-semibold text-lg mb-2 text-red-800">
                ❌ Connection Failed
              </h2>
              <div className="space-y-2 text-sm">
                <p><strong>Error:</strong> {results.error.message}</p>
                {results.error.code && <p><strong>Code:</strong> {results.error.code}</p>}
                {results.error.status && <p><strong>Status:</strong> {results.error.status}</p>}
                {results.error.response && (
                  <div>
                    <strong>Response:</strong>
                    <pre className="bg-white p-3 rounded text-xs overflow-auto mt-1">
                      {JSON.stringify(results.error.response, null, 2)}
                    </pre>
                  </div>
                )}
              </div>

              <div className="mt-4 p-3 bg-white rounded">
                <p className="font-semibold mb-2">Troubleshooting:</p>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  <li>Check if backend server is running</li>
                  <li>Verify REACT_APP_API_URL in .env file</li>
                  <li>Check CORS settings in backend</li>
                  <li>Open browser DevTools console for more details</li>
                  <li>Check Network tab for failed requests</li>
                </ul>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-4">
            <button
              onClick={testConnection}
              className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition"
            >
              🔄 Retry Test
            </button>
            <button
              onClick={() => window.open(results.apiUrl.replace('/api', '') + '/api/health', '_blank')}
              className="px-6 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition"
            >
              🌐 Open Health Check in Browser
            </button>
          </div>

          {/* Console Logs Info */}
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600">
              💡 <strong>Tip:</strong> Open browser console (F12) to see detailed logs
              of all requests and responses.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConnectionTest;
